import { Button, Combobox, Group, SimpleGrid, Tooltip, useMatches } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { CarCard } from "../CarCard";
import { CarComparator } from "../CarComparator";
import { CarTableRow } from "../CarTableRow";
import { CarsTableFilters, CarsTableFiltersPayload } from "./components";

import { Collapsable, EmptyState, ListTable, Modal, useColumnSorter } from "@/components/elements";
import PopoverTable from "@/components/elements/PopoverTable/PopoverTable";
import {
  IconFilter,
  IconFilterFilled,
  IconGridFour,
  IconList,
  IconLock,
  IconTags,
} from "@/components/icons";
import { KPI_COLUMNS_BASE, KPI_COLUMNS_FREE } from "@/config/plans.config";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type {
  ListTableColumnSortBy,
  ListTableComparisonProps,
  ListTableTh,
} from "@/components/elements";
import type { Car, CarColumns } from "@/types/car";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

type CarsView = "cards" | "table";

export type CarsTableProps = {
  cars?: Car[];
  columns?: CarColumns[];
  columnsDisabled?: CarColumns[];
  conditions?: Car["condition"][];
  defaultView?: CarsView;
  getPath?: (carId: string) => string;
  namespace?: string;
  onAddCar?: () => void;
  onCarsCompare?: (car1Id: string, car2Id: string) => void;
  onConditionChange?: (condition?: Car["condition"]) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (query: string) => void;
  onStatusChange?: (status?: Car["status"]) => void;
  page?: number;
  pageSize?: number;
  statuses?: Car["status"][];
  totalCars?: number;
  totalCarsUnfiltered?: number;
  garageId?: string;
  canAccess?: boolean;
} & MantineStyleProps;

export const CarsTable: FC<CarsTableProps> = ({
  cars = [],
  columns = [],
  columnsDisabled,
  conditions = [],
  defaultView = "table",
  getPath,
  namespace = "garage-table",
  onAddCar,
  onCarsCompare,
  onConditionChange,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onStatusChange,
  page = 1,
  pageSize = 25,
  statuses = [],
  totalCars,
  totalCarsUnfiltered,
  garageId,
  canAccess = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [carsView, setCarsView] = useState(defaultView);
  const [status, setStatus] = useState<Car["status"]>();
  const [condition, setCondition] = useState<Car["condition"]>();
  const [opened, { open, close }] = useDisclosure();
  const isMobile = useMatches({ base: true, md: false });
  const hasComparison = !!onCarsCompare;
  const { getSortById, getUpdateSort } = useColumnSorter<Car, CarColumns>(`${namespace}-sort`);

  const { data: plans } = usePlansUser();
  const userPlan = plans?.name;
  const canCompareByPlan = userPlan === "collector" || userPlan === "professional";

  const [popoverOpened, setPopoverOpened] = useState(false);

  const tableColumns: ListTableTh[] = columns.map((id) => ({
    filter: getColumnFilter(id),
    id,
    label: t(`car.${id}`),
    sorter: getColumnSorter(id),
  }));
  // If the user is on the starter plan, also lock the "condition" column
  const KPI_COLUMNS = [
    ...(userPlan === "starter" ? KPI_COLUMNS_FREE : KPI_COLUMNS_BASE),
  ] as CarColumns[];

  const orderedColumnIds = canAccess
    ? columns
    : [
        ...columns.filter((c) => !KPI_COLUMNS.includes(c as CarColumns)),
        ...columns.filter((c) => KPI_COLUMNS.includes(c as CarColumns)),
      ];

  const orderedTableColumns: ListTableTh[] = orderedColumnIds.map((id) => {
    const isLocked = !canAccess && KPI_COLUMNS.includes(id as CarColumns);
    return {
      filter: getColumnFilter(id as CarColumns),
      id,
      label: t(`car.${id}`),
      ...(isLocked
        ? { icon: <IconLock size={22} style={{ flexShrink: 0, opacity: 0.6 }} /> }
        : { sorter: getColumnSorter(id as CarColumns) }),
    };
  });

  const originalTotal = typeof totalCars === "number" ? totalCars : cars.length;
  const unfilteredTotal =
    typeof totalCarsUnfiltered === "number" ? totalCarsUnfiltered : originalTotal;
  // Preserve the initial unfiltered total so UI controls (like the search input)
  // remain visible even when a search returns 0 results. This value is captured
  // once from the first available total (unfiltered/totalCars/cars.length).
  const initialUnfilteredRef = useRef<number | null>(null);

  useEffect(() => {
    // Capture an initial unfiltered total once, but allow updating from 0 -> >0
    // so controls (search/filter/etc.) reappear when the first asset is added.
    if (initialUnfilteredRef.current === null || initialUnfilteredRef.current === 0) {
      const initial =
        typeof totalCarsUnfiltered === "number"
          ? totalCarsUnfiltered
          : typeof totalCars === "number"
            ? totalCars
            : cars.length > 0
              ? cars.length
              : null;

      if (typeof initial === "number") {
        initialUnfilteredRef.current = initial;
      }
    }
  }, [totalCarsUnfiltered, totalCars, cars.length]);
  const hasPageSizeChanger = !!onPageSizeChange && originalTotal > 10;
  const hasPagination = !!onPageChange && originalTotal > pageSize;
  const showControls =
    initialUnfilteredRef.current === null
      ? typeof totalCarsUnfiltered === "number"
        ? totalCarsUnfiltered > 0
        : typeof totalCars === "number"
          ? totalCars > 0
          : cars.length > 0
      : initialUnfilteredRef.current > 0;

  function getColumnSorter(id: CarColumns) {
    return (
      <ListTable.ColumnSorter
        tooltip={t("garage.table_sort_tooltip")}
        sortBy={getSortById(id)}
        onSortChange={onChangeSortBy(id)}
      />
    );
  }

  function getColumnFilter(id: CarColumns) {
    switch (id) {
      case "status":
        return statuses.length > 0 ? (
          <ListTable.ColumnFilter
            filtered={!!status}
            label={t("car.status_filter")}
            tooltip={t("car.status_filter")}
          >
            <Combobox
              onOptionSubmit={(value) =>
                handleStatusChange(status === value ? undefined : (value as Car["status"]))
              }
            >
              <Combobox.Options>
                {statuses.map((value) => (
                  <Combobox.Option key={value} value={value} active={value === status}>
                    {t(`car.statuses.${value}`, value)}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Combobox>
          </ListTable.ColumnFilter>
        ) : undefined;
      case "condition":
        return conditions.length > 0 ? (
          <ListTable.ColumnFilter
            filtered={!!condition}
            label={t("car.condition_filter")}
            tooltip={t("car.condition_filter")}
          >
            <Combobox
              onOptionSubmit={(value) =>
                handleConditionChange(condition === value ? undefined : (value as Car["condition"]))
              }
            >
              <Combobox.Options>
                {conditions.map((value) => (
                  <Combobox.Option key={value} value={value} active={value === condition}>
                    {t(`car.conditions.${value}`, value)}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </Combobox>
          </ListTable.ColumnFilter>
        ) : undefined;
      default:
        return undefined;
    }
  }

  function handleCarsCompare(car1Id: string, car2Id: string) {
    onCarsCompare?.(car1Id, car2Id);
    close();
  }

  function handleConditionChange(condition?: Car["condition"]) {
    setCondition(condition);
    onConditionChange?.(condition);
  }

  function handleFiltersChange({ condition, status }: CarsTableFiltersPayload) {
    handleConditionChange(condition);
    handleStatusChange(status);
  }

  function handleStatusChange(status?: Car["status"]) {
    setStatus(status);
    onStatusChange?.(status);
  }

  function onChangeSortBy(id: CarColumns) {
    return (sortBy?: ListTableColumnSortBy) => {
      getUpdateSort(id)(sortBy);
    };
  }

  return (
    <>
      {/* pass disabled columns so they can't be toggled/reordered; KPI columns are disabled when canAccess is false */}
      <ListTable.Context
        columns={canAccess ? tableColumns : orderedTableColumns}
        disabled={
          columnsDisabled
            ? canAccess
              ? columnsDisabled
              : [...columnsDisabled, ...KPI_COLUMNS]
            : canAccess
              ? []
              : KPI_COLUMNS
        }
        namespace={namespace}
      >
        {({ filteredColumns, headingColumns, onFilteredColumnsChange }) => {
          const columnsId = headingColumns.map(({ id }) => id) as CarColumns[];

          return (
            <ListTable.SelectionContext data={cars.map((car) => car.identifier)} maxSelection={2}>
              {({ checkStatus, getRowCheckbox, isSelected, onToggleAll, reset, selected }) => {
                const comparisonProps = hasComparison
                  ? ({
                      checkStatus,
                      hasComparison: true,
                      onToggleAll,
                    } as ListTableComparisonProps)
                  : {};

                return (
                  <>
                    <Collapsable>
                      {showControls && (
                        <Group gap={isMobile ? "xs" : "md"} p="lg">
                          {onSearch && (
                            <ListTable.SearchInput
                              onChange={onSearch}
                              placeholder={t("garage.search_placeholder")}
                              resetTooltip={t("garage.search_reset_tooltip")}
                              updateOnChange
                            />
                          )}
                          {statuses.length > 0 && (
                            <Collapsable.ToggleButton px="xs">
                              {status ? <IconFilterFilled /> : <IconFilter />}
                            </Collapsable.ToggleButton>
                          )}
                          {isMobile ? (
                            <>
                              <PopoverTable
                                popoverOpened={popoverOpened}
                                setPopoverOpened={() => setPopoverOpened((prev) => !prev)}
                                actionInPending={false}
                                filteredColumns={filteredColumns}
                                onFilteredColumnsChange={onFilteredColumnsChange}
                              />
                            </>
                          ) : (
                            <>
                              <ListTable.ColumnsOrganizer
                                columns={filteredColumns}
                                label={t("common.customizeColumns")}
                                onChange={onFilteredColumnsChange}
                                tooltip={t("common.organizeColumns")}
                              />
                            </>
                          )}
                          <Group gap="sm">
                            <Button
                              px="xs"
                              onClick={() => setCarsView("cards")}
                              variant={carsView === "cards" ? "light" : "subtle"}
                            >
                              <IconGridFour />
                            </Button>
                            <Button
                              px="xs"
                              onClick={() => setCarsView("table")}
                              variant={carsView === "table" ? "light" : "subtle"}
                            >
                              <IconList />
                            </Button>
                          </Group>
                          <Tooltip
                            label={t("plans.contentLocker.upgradeToAccess")}
                            disabled={canCompareByPlan}
                            withArrow
                            events={{ hover: true, focus: true, touch: true }}
                            position="top"
                            offset={6}
                            maw={300}
                            openDelay={100}
                            multiline
                          >
                            <Button
                              display={isMobile ? "none" : "inherit"}
                              onClick={open}
                              leftSection={<IconTags />}
                              disabled={cars.length < 2 || !canCompareByPlan}
                              aria-label={t("garage.compare")}
                            >
                              {t("garage.compare")}
                            </Button>
                          </Tooltip>
                          <Tooltip
                            label={t("plans.contentLocker.upgradeToAccess")}
                            disabled={canCompareByPlan}
                            withArrow
                            events={{ hover: true, focus: true, touch: true }}
                            position="top"
                            offset={-8}
                            maw={300}
                            openDelay={100}
                            multiline
                          >
                            <Button
                              px="xs"
                              onClick={open}
                              display={!isMobile ? "none" : "inherit"}
                              disabled={cars.length < 2 || !canCompareByPlan}
                            >
                              <IconTags />
                            </Button>
                          </Tooltip>
                        </Group>
                      )}
                      <Collapsable.Collapse>
                        <CarsTableFilters
                          condition={condition}
                          conditions={conditions}
                          onCancel={() =>
                            handleFiltersChange({ condition: undefined, status: undefined })
                          }
                          onSubmit={handleFiltersChange}
                          status={status}
                          statuses={statuses}
                        />
                      </Collapsable.Collapse>
                    </Collapsable>

                    {carsView === "table" &&
                      (cars.length > 0 ? (
                        <ListTable
                          data={{
                            head: headingColumns,
                            body: cars.map((car) => (
                              <CarTableRow
                                key={car.identifier}
                                checkbox={getRowCheckbox(car.identifier)}
                                columns={columnsId}
                                data={car}
                                canAccess={canAccess}
                                onClick={
                                  getPath ? () => navigate(getPath(car.identifier)) : undefined
                                }
                                selected={isSelected(car.identifier)}
                                to={getPath?.(car.identifier)}
                              />
                            )),
                          }}
                          tableProps={{ highlightOnHover: true }}
                          {...comparisonProps}
                        />
                      ) : unfilteredTotal === 0 ? (
                        <EmptyState type="noResults" text={t("garage.no_results")}>
                          {onAddCar && (
                            <Button
                              variant="filled"
                              onClick={onAddCar}
                              aria-label={t("dashboard.cars.create_button")}
                            >
                              {t("dashboard.cars.create_button")}
                            </Button>
                          )}
                        </EmptyState>
                      ) : (
                        <EmptyState
                          type="noResults"
                          text={t("garage.search_no_results", t("garage.no_results"))}
                        />
                      ))}

                    {carsView === "cards" &&
                      (cars.length > 0 ? (
                        <SimpleGrid
                          cols={{ base: 2, xs: 3, sm: 3, md: 4 }}
                          spacing="lg"
                          px="lg"
                          pb="lg"
                        >
                          {cars.map((car) => (
                            <CarCard
                              key={car.identifier}
                              badge={car.nickname}
                              make={car.make}
                              name={car.model}
                              onClick={
                                getPath ? () => navigate(getPath(car.identifier)) : undefined
                              }
                              carId={car.identifier}
                              marketCarId={car.taxonomy_id}
                            />
                          ))}
                        </SimpleGrid>
                      ) : unfilteredTotal === 0 ? (
                        <EmptyState type="noResults" text={t("garage.no_results")}>
                          {onAddCar && (
                            <Button
                              variant="filled"
                              onClick={onAddCar}
                              aria-label={t("dashboard.cars.create_button")}
                            >
                              {t("dashboard.cars.create_button")}
                            </Button>
                          )}
                        </EmptyState>
                      ) : (
                        <EmptyState
                          type="noResults"
                          text={t("garage.search_no_results", t("garage.no_results"))}
                        />
                      ))}

                    {cars.length > 0 && (hasPageSizeChanger || hasPagination) && (
                      <Group justify="flex-end" p="lg">
                        {hasPageSizeChanger && (
                          <ListTable.PageSizeChanger
                            label={t("components.ListTable.page_size_changer_label")}
                            onChange={onPageSizeChange}
                            pageSize={pageSize}
                          />
                        )}
                        {hasPagination && (
                          <ListTable.Pagination
                            divider={t("components.ListTable.pagination_divider")}
                            page={page}
                            pageSize={pageSize}
                            onChange={onPageChange}
                            total={totalCars}
                          />
                        )}
                      </Group>
                    )}

                    {hasComparison && (
                      <Modal
                        opened={opened}
                        onClose={close}
                        centered
                        icon={IconTags}
                        title={t("garage.compare_modal")}
                      >
                        <CarComparator
                          defaultCar1Id={selected[0]}
                          defaultCar2Id={selected[1]}
                          onCancel={close}
                          onCompare={(car1Id, car2Id) => {
                            handleCarsCompare(car1Id, car2Id);
                            reset();
                          }}
                          garageId={garageId}
                          carsData={cars.map((car) => ({
                            label: `${car.make} ${car.model}${car.nickname ? ` (${car.nickname})` : ""}`,
                            value: car.identifier,
                          }))}
                        />
                      </Modal>
                    )}
                  </>
                );
              }}
            </ListTable.SelectionContext>
          );
        }}
      </ListTable.Context>
    </>
  );
};
